import { FieldState } from '../models/field-state';

export enum MessageLevel {
  Info = 0,
  Warning = 1,
  Error = 2,
}

export enum Stop {
  IfValid,
  IfInvalid,
  Always,
  Never,
}

export interface IValidationResult {
  isValid: boolean;
  //isStop: boolean; //the flow must not be modified when isStop is null
  message: string;
  level: MessageLevel;
}

export interface IDeferred {
  notify(c?);
  resolve(c?);
  reject(c?);
  promise: IPromise;
}

export interface IObserver {
  destination: IDestination;
}

export interface IDestination {
  next(c?);
  error(c?);
  complete();
}

export interface IPromise {
  catch(errorCallback);
  finally(callback, notifyCallback?);
  then(successCallback, errorCallback?, notifyCallback?);
}

export interface IValidator {
  isAsync: boolean;
  promiser: () => IPromise;
  syncValidator(): IValidationResult;
  asyncValidator(response): IValidationResult;
  isStop: boolean;
  scope: FieldState;
}

export interface IFieldStateDetail {
  isPristine: boolean;
  isEdited: boolean;
}

export interface IValidationSetup extends IFieldStateDetail {
  validationSetup: any[][];
}

export class ValidatedField<TValue> implements IValidationSetup {
  value: TValue;
  initialValue: TValue;
  isValid = true;
  messageLevel = MessageLevel.Info;
  message = null;
  isPristine = true;
  validationSetup: any[][];

  get messageClass(): string {
    switch (this.messageLevel) {
      case MessageLevel.Info:
        return 'info';
      case MessageLevel.Error:
        return 'error';
      case MessageLevel.Warning:
        return 'warning';
    }
    return null;
  }

  get isEdited(): boolean {
    return this.initialValue !== this.value;
  }

  constructor(
    group: IValidationSetup[] = undefined,
    value: any = undefined,
    isValid: boolean = undefined,
    messageLevel: MessageLevel = undefined,
    message: string = undefined
  ) {
    this.initialValue = value;

    if (group !== void 0 && group != null) group.push(this);

    if (value !== void 0) this.value = value;

    if (isValid !== void 0) this.isValid = isValid;

    if (messageLevel !== void 0) this.messageLevel = messageLevel;

    if (message !== void 0) this.message = message;
  }
}

export class Validator {
  private static forValuePredicateBase<TValue>(
    scope: FieldState,
    isStop: (value: TValue) => boolean,
    getter: () => TValue,
    predicate: (value: TValue) => boolean,
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    var value = getter();
    return {
      syncValidator: () => {
        // console.log(value);
        var result = predicate(value);
        return result
          ? { isValid: true, message: null, level: null, scope: scope }
          : { isValid: false, message: message, level: level, scope: scope };
      },
      isAsync: false,
      promiser: null,
      asyncValidator: null,
      isStop: isStop(value),
      scope: scope,
    };
  }

  private static forPredicateBase<TValue>(
    scope: FieldState,
    isStop: boolean,
    predicate: () => boolean,
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    return {
      syncValidator: () => {
        var result = predicate();
        return result
          ? { isValid: true, message: null, level: null, scope: scope }
          : { isValid: false, message: message, level: level, scope: scope };
      },
      isAsync: false,
      promiser: null,
      asyncValidator: null,
      isStop: isStop,
      scope: scope,
    };
  }

  static forValuePredicate<TValue>(
    scope: FieldState,
    getter: () => TValue,
    predicate: (value: TValue) => boolean,
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    return this.forValuePredicateBase(
      scope,
      (value) => false,
      getter,
      predicate,
      message,
      level
    );
  }

  static forPredicate<TValue>(
    scope: FieldState,
    predicate: () => boolean,
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    return this.forPredicateBase(scope, false, predicate, message, level);
  }

  static forBool(
    scope: FieldState,
    getter: () => boolean,
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    return this.forValuePredicate(
      scope,
      getter,
      (value) => value,
      message,
      level
    );
  }

  static forNotRequired<TValue>(
    scope: FieldState,
    getter: () => TValue
  ): IValidator {
    return this.forValuePredicateBase(
      scope,
      (value) => value == null || <any>value == '',
      getter,
      (value) => value == null || <any>value == '' || <any>value,
      null,
      MessageLevel.Info
    );
  }

  static forRequired<TValue>(
    scope: FieldState,
    getter: () => TValue,
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    return this.forValuePredicate(
      scope,
      getter,
      (value) => !(value == null || <any>value === ''),
      message,
      level
    );
  }

  static forNotEmpty<TValue>(
    scope: FieldState,
    getter: () => TValue[],
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    return this.forValuePredicate(
      scope,
      getter,
      (value) => !(value == null || value.length === 0),
      message,
      level
    );
  }

  static forRequiredArray<TValue>(
    getter: () => TValue[],
    scope: FieldState,
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    return this.forValuePredicate(
      scope,
      getter,
      (value) => !(value == null || value.length === 0),
      message,
      level
    );
  }

  static forMinLength<TValue>(
    scope,
    getter: () => TValue,
    length: number,
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    return this.forValuePredicate(
      scope,
      getter,
      (value) => value != null && (<any>value).length >= length,
      message,
      level
    );
  }

  static forMaxLength<TValue>(
    scope,
    getter: () => TValue,
    length: number,
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    return this.forValuePredicate(
      scope,
      getter,
      (value) => value != null && (<any>value).length <= length,
      message,
      level
    );
  }

  static forPattern<TValue>(
    scope,
    getter: () => TValue,
    pattern: any,
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    return this.forValuePredicate(
      scope,
      getter,
      (value) => value !== void 0 && value != null && pattern.test(value),
      message,
      level
    );
  }

  static forNoPattern<TValue>(
    scope,
    getter: () => TValue,
    pattern: any,
    message: string,
    level: MessageLevel = MessageLevel.Error
  ): IValidator {
    return this.forValuePredicate(
      scope,
      getter,
      (value) => value !== void 0 && value != null && !pattern.test(value),
      message,
      level
    );
  }

  static forAsync<TAsyncResult>(
    scope: FieldState,
    promiser: () => IPromise,
    success: (response: TAsyncResult) => IValidationResult
  ): IValidator {
    return {
      isAsync: true,
      promiser: promiser,
      asyncValidator: success,
      syncValidator: null,
      isStop: false,
      scope: scope,
    };
  }

  static forBypass<TValue>(fieldScope: FieldState): IValidator {
    return {
      syncValidator: () => {
        return { isValid: true, message: null, level: null, scope: fieldScope };
      },
      isAsync: false,
      promiser: null,
      asyncValidator: null,
      isStop: true,
      scope: fieldScope,
    };
  }
}
